import React, { useState, useEffect, useRef } from "react";
import styles from "./MobileCartSummary.module.css";

interface MobileCartSummaryProps {
  isCheckoutDisabled: boolean;
  onCheckout: () => void;
  total: number;
  subtotal: number;
  totalProducts: number;
  totalDeliveryFee: number;
  isUpdatingFees: boolean;
  hasLocationSet: boolean;
  allGroupsHaveDeliveryFees: boolean;
  hasGroupsMissingDates?: boolean;
}

const MobileCartSummary: React.FC<MobileCartSummaryProps> = ({
  isCheckoutDisabled,
  onCheckout,
  total,
  subtotal,
  totalProducts,
  totalDeliveryFee,
  isUpdatingFees,
  hasLocationSet,
  allGroupsHaveDeliveryFees,
  hasGroupsMissingDates = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = () => {
    setIsTransitioning(true);
    setIsExpanded(!isExpanded);

    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300); // Match the CSS transition duration
  };

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        summaryRef.current &&
        !summaryRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
        setIsTransitioning(true);
        setTimeout(() => setIsTransitioning(false), 300);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <>
      {isExpanded && (
        <div
          className={`${styles.overlay} ${isExpanded ? styles.visible : ""}`}
          onClick={() => {
            setIsExpanded(false);
            setIsTransitioning(true);
            setTimeout(() => setIsTransitioning(false), 300);
          }}
        />
      )}

      <div
        ref={summaryRef}
        className={`${styles.mobileSummary} ${
          isExpanded ? styles.expanded : ""
        } ${isTransitioning ? styles.transitioning : ""}`}
      >
        <div
          className={styles.handle}
          onClick={toggleExpanded}
          aria-label={isExpanded ? "Collapse summary" : "Expand summary"}
        >
          <span
            className={`${styles.chevron} ${
              isExpanded ? styles.down : styles.up
            }`}
          ></span>
        </div>

        <div ref={contentRef} className={styles.expandedContent}>
          {/* Expanded content - visible when expanded */}
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>
              {totalProducts} {totalProducts === 1 ? "producto" : "productos"}
            </span>
            <span className={styles.detailValue}>S/ {subtotal.toFixed(2)}</span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Envío:</span>
            <span className={styles.detailValue}>
              {!hasLocationSet ? (
                <span className={styles.pendingCalculation}>Por calcular</span>
              ) : isUpdatingFees ? (
                <span className={styles.loadingFee}>
                  <span className={styles.spinningIcon}></span>
                  Calculando
                </span>
              ) : !allGroupsHaveDeliveryFees ? (
                <span className={styles.pendingCalculation}>Por calcular</span>
              ) : totalDeliveryFee > 0 ? (
                `S/ ${totalDeliveryFee.toFixed(2)}`
              ) : (
                <span className={styles.freeShipping}>Gratis</span>
              )}
            </span>
          </div>
        </div>

        <div className={styles.checkoutBar}>
          <div className={styles.totalSection}>
            <span className={styles.totalLabel}>Total:</span>
            <span className={styles.totalValue}>
              {!hasLocationSet ||
              isUpdatingFees ||
              !allGroupsHaveDeliveryFees ? (
                <span className={styles.calculatingTotal}>
                  {isUpdatingFees ? (
                    <>
                      <span className={styles.spinningIcon}></span>
                      Calculando
                    </>
                  ) : (
                    "Por calcular"
                  )}
                </span>
              ) : (
                <span className={styles.finalPrice}>S/ {total.toFixed(2)}</span>
              )}
            </span>
          </div>

          <button
            onClick={onCheckout}
            className={`${styles.checkoutButton} mainButton ${
              isCheckoutDisabled ? styles.checkoutButtonDisabled : ""
            }`}
            disabled={isCheckoutDisabled || isUpdatingFees}
          >
            Proceder al pago
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileCartSummary;
